import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  createSupportTicketApi,
  getMySupportTicketsApi,
  getSupportTicketApi,
  replySupportTicketApi,
} from '../../../util/api';
import BookingSearchSelect from './BookingSearchSelect';
import css from './SupportModal.module.css';

const CATEGORIES = [
  { value: 'booking', label: 'Booking' },
  { value: 'payment_refund', label: 'Payment / Refund' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'subscription_refund_request', label: 'Subscription Refund Request' },
  { value: 'account', label: 'Account' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];

const formatDateTime = value => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
};

const SupportModal = ({ currentUser }) => {
  const [view, setView] = useState('new');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('other');
  const [priority, setPriority] = useState('normal');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [myTickets, setMyTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);

  const loadMyTickets = useCallback(async () => {
    try {
      setIsLoadingTickets(true);
      const response = await getMySupportTicketsApi();
      setMyTickets(response?.data || []);
    } catch (err) {
      setErrorMessage(err?.error || 'Failed to load your tickets');
    } finally {
      setIsLoadingTickets(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'my') {
      loadMyTickets();
    }
  }, [view, loadMyTickets]);

  const loadTicketThread = useCallback(async ticketId => {
    if (!ticketId) return;
    try {
      const response = await getSupportTicketApi(ticketId);
      setTicketDetail(response?.data?.ticket || null);
      setThreadMessages(response?.data?.messages || []);
    } catch (err) {
      setErrorMessage(err?.error || 'Failed to load ticket');
    }
  }, []);

  useEffect(() => {
    if (selectedTicketId) {
      loadTicketThread(selectedTicketId);
    }
  }, [selectedTicketId, loadTicketThread]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      setErrorMessage('Subject and message are required');
      return;
    }
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const response = await createSupportTicketApi({
        subject: subject.trim(),
        message: message.trim(),
        category,
        priority,
        relatedBookingId: selectedBooking?.id || undefined,
        relatedBookingNumber: selectedBooking?.bookingCode || undefined,
      });
      setSuccessMessage(
        `Ticket ${response?.data?.ticketNumber || ''} submitted successfully. Our team will respond soon.`
      );
      setSubject('');
      setMessage('');
      setSelectedBooking(null);
      setView('my');
      await loadMyTickets();
      if (response?.data?.id) {
        setSelectedTicketId(response.data.id);
      }
    } catch (err) {
      setErrorMessage(err?.error || 'Failed to submit ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicketId || !replyText.trim()) return;
    try {
      setIsSubmitting(true);
      await replySupportTicketApi(selectedTicketId, { message: replyText.trim() });
      setReplyText('');
      await loadTicketThread(selectedTicketId);
      await loadMyTickets();
    } catch (err) {
      setErrorMessage(err?.error || 'Failed to send reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className={css.root}>
        <p>Please log in to contact support.</p>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <h2 className={css.title}>Support</h2>
      <p className={css.subtitle}>Submit a ticket or view your existing support conversations.</p>

      <div className={css.tabs}>
        <button
          type="button"
          className={classNames(css.tabButton, { [css.tabButtonActive]: view === 'new' })}
          onClick={() => setView('new')}
        >
          New Ticket
        </button>
        <button
          type="button"
          className={classNames(css.tabButton, { [css.tabButtonActive]: view === 'my' })}
          onClick={() => setView('my')}
        >
          My Tickets
        </button>
      </div>

      <div className={css.modalBody}>
      {successMessage ? <div className={css.successMessage}>{successMessage}</div> : null}
      {errorMessage ? <div className={css.errorMessage}>{errorMessage}</div> : null}

      {view === 'new' ? (
        <form onSubmit={handleSubmit}>
          <div className={css.field}>
            <label className={css.label} htmlFor="support-subject">
              Subject
            </label>
            <input
              id="support-subject"
              className={css.input}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              required
            />
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="support-category">
              Category
            </label>
            <select
              id="support-category"
              className={css.select}
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              {CATEGORIES.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="support-priority">
              Priority
            </label>
            <select
              id="support-priority"
              className={css.select}
              value={priority}
              onChange={e => setPriority(e.target.value)}
            >
              {PRIORITIES.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="support-booking">
              Related booking (optional)
            </label>
            <BookingSearchSelect
              currentUser={currentUser}
              value={selectedBooking}
              onChange={setSelectedBooking}
              disabled={isSubmitting}
            />
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="support-message">
              Message
            </label>
            <textarea
              id="support-message"
              className={css.textarea}
              value={message}
              onChange={e => setMessage(e.target.value)}
              required
            />
          </div>
          <div className={css.actions}>
            <button type="submit" className={css.submitButton} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      ) : (
        <>
          {isLoadingTickets ? <p>Loading your tickets...</p> : null}
          <div className={css.ticketList}>
            {myTickets.map(ticket => (
              <button
                type="button"
                key={ticket.id}
                className={classNames(css.ticketItem, {
                  [css.ticketItemActive]: String(selectedTicketId) === String(ticket.id),
                })}
                onClick={() => {
                  setSelectedTicketId(ticket.id);
                  setSuccessMessage('');
                  setErrorMessage('');
                }}
              >
                <div className={css.ticketItemTitle}>
                  {ticket.ticketNumber} · {ticket.subject}
                </div>
                <div className={css.ticketItemMeta}>
                  {ticket.status} · {formatDateTime(ticket.lastMessageAt)}
                </div>
              </button>
            ))}
            {!isLoadingTickets && myTickets.length === 0 ? (
              <p>No support tickets yet.</p>
            ) : null}
          </div>

          {selectedTicketId && ticketDetail ? (
            <>
              <div className={css.thread}>
                {threadMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={classNames(css.messageRow, {
                      [css.messageRowAdmin]: msg.senderRole === 'admin' || msg.senderRole === 'owner',
                    })}
                  >
                    <div className={css.messageMeta}>
                      {msg.senderName || msg.senderRole} · {formatDateTime(msg.createdAt)}
                    </div>
                    <div>{msg.message}</div>
                  </div>
                ))}
              </div>
              {ticketDetail.status !== 'closed' ? (
                <div className={css.field}>
                  <label className={css.label} htmlFor="support-reply">
                    Reply
                  </label>
                  <textarea
                    id="support-reply"
                    className={css.textarea}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                  />
                  <div className={css.actions}>
                    <button
                      type="button"
                      className={css.submitButton}
                      onClick={handleReply}
                      disabled={isSubmitting || !replyText.trim()}
                    >
                      Send Reply
                    </button>
                  </div>
                </div>
              ) : (
                <p>This ticket is closed.</p>
              )}
            </>
          ) : null}
        </>
      )}
      </div>
    </div>
  );
};

export default SupportModal;
